import { isAxiosError } from 'axios';
import classNames from 'classnames';

import BackIcon from '../assets/icons/back.svg?react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { CommonApi, type PatientDTO, PeopleApi } from '../api';
import { PrivateRoute } from '../components';
import { RoutePath } from '../config';
import { DoctorInnerPageLayout } from '../layouts';
import type { Patient } from '../model';
import {
  PENDING_SELECT_PATIENT_PHONE_STORAGE_KEY,
  normalizePhoneNumber,
  parseCreatedPatientIdFromResponse,
  patientFormValidation,
  ruPhoneDigitsEqual,
} from '../utils';

type CreatePatientForm = Pick<PatientDTO, 'username'>;

const PLACEHOLDER_SURNAME = 'Клиент';
const RESOLVE_PATIENT_RETRIES = 5;
const RESOLVE_PATIENT_PAGE_SIZE = 100;

const wait = (ms: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const removePendingPatientPhone = () => {
  try {
    sessionStorage.removeItem(PENDING_SELECT_PATIENT_PHONE_STORAGE_KEY);
  } catch {
    return;
  }
};

const setPendingPatientPhone = (phone: string) => {
  try {
    sessionStorage.setItem(PENDING_SELECT_PATIENT_PHONE_STORAGE_KEY, phone);
  } catch {
    return;
  }
};

const patientPhoneDigits = (patient: Patient): string => {
  const contact = patient.contacts.find((c) => c.contact_type.id === 18);
  if (contact?.value) {
    return normalizePhoneNumber(contact.value);
  }
  return normalizePhoneNumber(patient.phone ?? '');
};

const findPatientIdByPhone = async (
  phone: string,
): Promise<number | undefined> => {
  const wanted = normalizePhoneNumber(phone);
  if (!wanted) return undefined;

  for (let attempt = 0; attempt < RESOLVE_PATIENT_RETRIES; attempt += 1) {
    const searchResult = await PeopleApi.getPatients(
      wanted,
      1,
      RESOLVE_PATIENT_PAGE_SIZE,
    );
    const candidates = [...searchResult.items];

    for (let page = 1; page <= 5; page += 1) {
      const pageResult = await PeopleApi.getPatients(
        '',
        page,
        RESOLVE_PATIENT_PAGE_SIZE,
      );
      candidates.push(...pageResult.items);
      if (pageResult.items.length < RESOLVE_PATIENT_PAGE_SIZE) break;
    }

    const exact = candidates.find((patient) =>
      ruPhoneDigitsEqual(wanted, patientPhoneDigits(patient)),
    );
    if (exact?.id) {
      return exact.id;
    }

    if (searchResult.items.length === 1 && searchResult.items[0]?.id) {
      return searchResult.items[0].id;
    }

    await wait(300 * (attempt + 1));
  }

  return undefined;
};

export const DoctorCreatePatientPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<CreatePatientForm>({
    defaultValues: { username: '' },
  });
  const { data: genderList = [] } = useQuery({
    queryKey: [CommonApi.KEY, 'getReference', 107],
    queryFn: () => CommonApi.getReference(107),
  });
  const { mutate: createPatient } = useMutation({
    mutationFn: PeopleApi.createPatient,
    onSuccess(response, variables) {
      const parsedFromResponse = parseCreatedPatientIdFromResponse(response);
      queryClient.invalidateQueries({ queryKey: [PeopleApi.KEY] });
      toast.success('Клиент успешно добавлен');
      const digits = normalizePhoneNumber(variables.username ?? '');
      void (async () => {
        const newId =
          parsedFromResponse ??
          (digits ? await findPatientIdByPhone(digits) : undefined);

        if (newId) {
          removePendingPatientPhone();
          navigate(`${RoutePath.DoctorCreateOrder}?patientId=${newId}`);
          return;
        }

        if (digits) {
          setPendingPatientPhone(digits);
        }
        navigate(RoutePath.DoctorCreateOrder, {
          state: digits ? { pendingSelectByPhone: digits } : undefined,
        });
      })();
    },
    onError(error: unknown) {
      if (isAxiosError(error)) {
        const data = error.response?.data as
          | { message?: string; error?: string }
          | undefined;
        const msg = data?.message ?? data?.error;
        toast.error(
          typeof msg === 'string' && msg.trim()
            ? msg
            : 'Ошибка! Проверьте данные',
        );
        return;
      }
      toast.error('Ошибка! Проверьте данные');
    },
  });
  const onSubmit: SubmitHandler<CreatePatientForm> = useCallback(
    (data) => {
      const phone = normalizePhoneNumber(data.username);
      const genderId = genderList[0]?.id ?? 1;
      const body: PatientDTO = {
        surname: PLACEHOLDER_SURNAME,
        name: '',
        midname: '',
        gender_id: genderId,
        birthdate: null,
        code: '',
        email: '',
        username: phone,
        contacts: [
          {
            contact_type: { id: 18 },
            value: phone,
          },
        ],
        documents: [],
      };
      createPatient(body);
    },
    [createPatient, genderList],
  );

  return (
    <PrivateRoute redirectPath={RoutePath.DoctorLogin}>
      <DoctorInnerPageLayout>
        <div className="grid grid-cols-2">
          <div className="rounded-[16px] bg-brand-6">
            <header className="p-[20px]">
              <button
                className="flex items-center gap-[6px] text-[20px] font-bold"
                onClick={() => navigate(-1)}
                type="button"
              >
                <BackIcon />
                Вернуться назад
              </button>
            </header>
            <form
              className="grid gap-[10px] p-[20px]"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div>
                <Controller
                  control={control}
                  name="username"
                  rules={patientFormValidation.username}
                  render={({ field, fieldState }) => (
                    <InputMask
                      {...field}
                      alwaysShowMask
                      className={classNames(
                        'input',
                        fieldState.invalid && 'input--invalid',
                      )}
                      mask="+7 999 999 99 99"
                      maskPlaceholder={null}
                      placeholder="Телефон"
                      type="tel"
                      value={field.value ?? ''}
                    />
                  )}
                />
                {errors.username && (
                  <p className="text-red-500 mt-[10px] text-sm">
                    {errors.username.message}
                  </p>
                )}
              </div>

              <button
                className="button button--block button--primary mt-[20px]"
                type="submit"
              >
                Добавить клиента
              </button>
            </form>
          </div>
        </div>
      </DoctorInnerPageLayout>
    </PrivateRoute>
  );
};
